import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  bomInventoryQty,
  ProductClient,
  type BomPartDTO,
  type PartClientPriceContractDTO,
  type ProductDTO,
} from "../../api/product";

/**
 * Contracted client prices for this SKU. Managed in the ERP, so this is a pure
 * read view — there is nothing to edit from here.
 *
 * A grid rather than a plain table because a popular SKU carries dozens of
 * contracts, and the questions asked of it are "which client" and "what is the
 * lowest price", both of which need filtering and sorting to answer.
 */
export function ContractPriceTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductClient(http), [http]);
  const sku = draft.metadata?.sku ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["product", "contract-prices", sku],
    queryFn: () => client.contractPrices(sku),
    enabled: sku !== "",
  });

  // A discount of -1 means "no discount clause", not minus one percent.
  const discounted = (row: PartClientPriceContractDTO): number | null =>
    (row.part_discount ?? -1) >= 0
      ? (row.part_price ?? 0) * (1 - (row.part_discount ?? 0) / 100)
      : null;

  // One entry per client account, named and numbered, in name order.
  const clientOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of data ?? []) {
      const no = row.client?.cli_no ?? "";
      if (no && !seen.has(no)) seen.set(no, `${row.client?.cli_name1 ?? ""} · ${no}`);
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const cols = useMemo(() => {
    const c = defineColumns<PartClientPriceContractDTO>();
    return [
      c.custom(
        "client",
        // Keyed on the client number, not the name. Names are not unique in the
        // ERP: this SKU alone has three separate J.M. Clement accounts sharing
        // one name, so filtering by name pulled in all three and looked broken.
        (row) => row.client?.cli_no ?? "",
        (row) => (
          <span className="flex flex-col py-0.5">
            <span className="font-medium text-ink">{row.client?.cli_name1 ?? ""}</span>
            <span className="text-[12px] text-ink-muted">{row.client?.cli_no ?? ""}</span>
          </span>
        ),
        {
          headerKey: "column.client",
          filter: "select",
          // Built here rather than left to the table, which would label each
          // entry with the bare account number. Name first because that is what
          // the eye scans for, number after because that is what distinguishes
          // two accounts wearing the same one.
          filterOptions: clientOptions,
          // Sort by name, since the column reads as a name even though it keys
          // on the number.
          sortingFn: (a, b) =>
            (a.original.client?.cli_name1 ?? "").localeCompare(
              b.original.client?.cli_name1 ?? "",
            ),
          priority: 1,
        },
      ),
      c.text("currency", (row) => row.client?.cli_currency ?? "", {
        headerKey: "column.currency",
        filter: "select",
        priority: 2,
      }),
      c.custom(
        "contract",
        (row) => `${row.price_contract?.no ?? ""} ${row.price_contract?.description ?? ""}`,
        (row) => (
          <span className="flex flex-col py-0.5">
            <span className="text-ink">{row.price_contract?.no ?? ""}</span>
            <span className="truncate text-[12px] text-ink-muted">
              {row.price_contract?.description ?? ""}
            </span>
          </span>
        ),
        { headerKey: "product.contract.contract", priority: 2 },
      ),
      c.text(
        "period",
        (row) =>
          row.price_contract?.start_date || row.price_contract?.end_date
            ? `${row.price_contract?.start_date ?? ""} → ${row.price_contract?.end_date ?? ""}`
            : "",
        { headerKey: "product.contract.period", priority: 3 },
      ),
      c.number("price", (row) => row.part_price ?? 0, {
        headerKey: "column.price",
        priority: 1,
      }),
      c.custom(
        "discounted",
        (row) => discounted(row) ?? 0,
        (row) => {
          const value = discounted(row);
          return value === null ? (
            <span className="text-ink-faint"></span>
          ) : (
            <span className="tabular">
              {value.toFixed(2)}
              <span className="ml-1 text-[11px] text-ink-faint">−{row.part_discount}%</span>
            </span>
          );
        },
        { headerKey: "product.contract.discounted", filter: "none", priority: 2 },
      ),
    ];
  }, [clientOptions]);

  return (
    <DataTable
      data={data ?? []}
      columns={cols}
      // `id` is the ERP contract *line*, and a contract is shared by every client
      // signed to it, so the same line comes back once per client. Keyed on
      // the line alone, the table (and React) treated those rows as one and
      // showed stale content once a filter changed which rows were visible.
      getRowId={(row) => `${row.id}:${row.client?.cli_id ?? ""}`}
      loading={isLoading}
      emptyMessage={t("product.contract.none", "No client price contracts for this SKU.")}
    />
  );
}

/** The bill of materials, from the ERP. Read-only reporting. */
export function BomTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductClient(http), [http]);
  const sku = draft.metadata?.sku ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["product", "bom", sku],
    queryFn: () => client.bomByMasterPart(sku),
    enabled: sku !== "",
  });

  const cols = useMemo(() => {
    const c = defineColumns<BomPartDTO>();
    return [
      c.text("part", (row) => row.part?.no, { headerKey: "product.bom.part", priority: 1 }),
      c.text("parent", (row) => row.parent_part?.no, { headerKey: "product.bom.parent", priority: 2 }),
      c.text("description", (row) => row.part?.description1, {
        headerKey: "product.bom.description",
        priority: 1,
      }),
      c.number("qty", (row) => row.budget_qty, { headerKey: "product.bom.qty", priority: 1 }),
      c.text("unit", (row) => row.budget_unit?.toUpperCase(), {
        headerKey: "product.bom.unit",
        filter: "select",
        priority: 3,
      }),
      c.number("inventory", (row) => bomInventoryQty(row), {
        headerKey: "column.inventory-qty",
        priority: 2,
      }),
      c.number("lead", (row) => row.avg_acquisition_time, {
        headerKey: "product.bom.lead",
        priority: 3,
      }),
    ];
  }, []);

  return (
    <DataTable
      data={data ?? []}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={isLoading}
      emptyMessage={t("product.bom.none", "No BOM lines for this SKU.")}
    />
  );
}
